// ABOUTME: React Router configuration for Obsidian BI frontend.
// ABOUTME: Defines routes for home, investigations, reports, admin, and PLG pages.

import { createBrowserRouter, Navigate } from 'react-router-dom';
import { RootLayout } from '@/components/layout/RootLayout';
import { HomePage } from '@/pages/HomePage';
import { InvestigationsPage } from '@/pages/InvestigationsPage';
import { InvestigationDetailPage } from '@/pages/InvestigationDetailPage';
import { ReportsPage } from '@/pages/ReportsPage';
import { ReportDetailPage } from '@/pages/ReportDetailPage';
import { ReportPrintPage } from '@/pages/ReportPrintPage';
import { PricingPage } from '@/pages/PricingPage';
import { OnboardingPage } from '@/pages/OnboardingPage';
import { UpgradePage } from '@/pages/UpgradePage';
import { BillingPage } from '@/pages/settings/BillingPage';
import { AdminDashboard } from '@/pages/admin/Dashboard';
import { WorkspaceList } from '@/pages/admin/WorkspaceList';
import { WorkspaceDetail } from '@/pages/admin/WorkspaceDetail';
import { AgentConfigPage, UsageAnalyticsPage } from '@/pages/admin/index';
import { DomainClustersPage } from '@/pages/admin/DomainClustersPage';
import { PlanList } from '@/pages/admin/PlanList';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { LoginPage } from '@/pages/LoginPage';
import { CallbackPage } from '@/pages/auth/CallbackPage';
import { VerifyEmailPage } from '@/pages/auth/VerifyEmailPage';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { SelfServiceRoute } from '@/components/auth/SelfServiceRoute';
import { AdminRoute } from '@/components/auth/AdminRoute';
import { CsvDatasourceDetailPage } from '@/pages/CsvDatasourceDetailPage';
import { DataSourcesPage } from '@/pages/DataSourcesPage';

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/auth/callback',
    element: <CallbackPage />,
  },
  {
    path: '/verify-email',
    element: <VerifyEmailPage />,
  },
  {
    // Print route for PDF generation - no layout wrapper, no auth (uses token param)
    path: '/reports/:id/print',
    element: <ReportPrintPage />,
  },
  {
    // Public shared report viewer - no auth required, lazy-loaded for bundle optimization
    path: '/r/:shareId',
    async lazy() {
      const { SharedReportPage } = await import('./pages/SharedReportPage');
      return { Component: SharedReportPage };
    },
  },
  {
    // Public demo page - simulated investigation experience, no auth required
    path: '/try',
    async lazy() {
      const { TryDemoPage } = await import('./pages/TryDemoPage');
      return { Component: TryDemoPage };
    },
  },
  {
    // Shopify OAuth callback proxy - forwards query params to backend API
    path: '/shopify/callback',
    async lazy() {
      const { ShopifyCallbackRedirect } = await import('./pages/ShopifyCallbackRedirect');
      return { Component: ShopifyCallbackRedirect };
    },
  },
  {
    // Shopify OAuth success page - no auth required
    path: '/shopify/connected',
    async lazy() {
      const { ShopifyConnectedPage } = await import('./pages/ShopifyConnectedPage');
      return { Component: ShopifyConnectedPage };
    },
  },
  {
    // Shopify OAuth error page - no auth required
    path: '/shopify/error',
    async lazy() {
      const { ShopifyErrorPage } = await import('./pages/ShopifyErrorPage');
      return { Component: ShopifyErrorPage };
    },
  },
  // Test route for ReportEditor - development only
  ...(import.meta.env.DEV
    ? [
        {
          path: '/test/report-editor',
          async lazy() {
            const { ReportEditorTestPage } = await import('@/pages/ReportEditorTestPage');
            return { Component: ReportEditorTestPage };
          },
        },
      ]
    : []),
  {
    path: '/',
    element: <RootLayout />,
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'investigations',
        element: (
          <ProtectedRoute>
            <InvestigationsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'investigations/:id',
        element: (
          <ProtectedRoute>
            <InvestigationDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports',
        element: (
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/:id',
        element: (
          <ProtectedRoute>
            <ReportDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'onboarding',
        element: (
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'data-sources',
        element: (
          <ProtectedRoute>
            <DataSourcesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'data-sources/:id',
        element: (
          <ProtectedRoute>
            <CsvDatasourceDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'pricing',
        element: (
          <ProtectedRoute>
            <SelfServiceRoute>
              <PricingPage />
            </SelfServiceRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: 'upgrade',
        element: (
          <ProtectedRoute>
            <SelfServiceRoute>
              <UpgradePage />
            </SelfServiceRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings/billing',
        element: (
          <ProtectedRoute>
            <BillingPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin',
        element: (
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        ),
      },
      {
        path: 'admin/workspaces',
        element: (
          <AdminRoute>
            <WorkspaceList />
          </AdminRoute>
        ),
      },
      {
        path: 'admin/workspaces/:workspaceId',
        element: (
          <AdminRoute>
            <WorkspaceDetail />
          </AdminRoute>
        ),
      },
      {
        path: 'admin/workspaces/:workspaceId/config',
        element: (
          <AdminRoute>
            <AgentConfigPage />
          </AdminRoute>
        ),
      },
      {
        path: 'admin/workspaces/:workspaceId/usage',
        element: (
          <AdminRoute>
            <UsageAnalyticsPage />
          </AdminRoute>
        ),
      },
      {
        path: 'admin/plans',
        element: (
          <AdminRoute>
            <PlanList />
          </AdminRoute>
        ),
      },
      {
        path: 'admin/domain-clusters',
        element: (
          <AdminRoute>
            <DomainClustersPage />
          </AdminRoute>
        ),
      },
      {
        path: '404',
        element: <NotFoundPage />,
      },
      {
        path: '*',
        element: <Navigate to="/404" replace />,
      },
    ],
  },
]);
